import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FocalPointPicker from "../../components/admin/FocalPointPicker";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { photoUrl } from "../../lib/assets";
import { ADMIN_HOME_HERO_PHOTOS_PATH } from "../../lib/adminPaths";

function keywordsToString(keywords) {
  return (keywords || []).join(", ");
}

export default function AdminHomeHeroPhotoEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replacingImage, setReplacingImage] = useState(false);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [focusX, setFocusX] = useState(50);
  const [focusY, setFocusY] = useState(50);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPhoto() {
      setLoading(true);
      setError("");
      try {
        const body = await apiFetch(`/admin/home-hero-photos/${id}`);
        if (cancelled) return;
        const data = body.data;
        setPhoto(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setKeywords(keywordsToString(data.keywords));
        setFocusX(typeof data.focusX === "number" ? data.focusX : 50);
        setFocusY(typeof data.focusY === "number" ? data.focusY : 50);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Couldn't load photo — try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPhoto();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleReplaceImage(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    setReplacingImage(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("photo", file);
      const body = await apiFetch(`/admin/home-hero-photos/${id}/replace-image`, {
        method: "POST",
        body: formData,
      });
      setPhoto(body.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Image replace failed — try again.");
    } finally {
      setReplacingImage(false);
      event.target.value = "";
    }
  }

  async function handleSave(event) {
    event.preventDefault();

    setSaving(true);
    setError("");

    try {
      await apiFetch(`/admin/home-hero-photos/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          keywords: keywords.trim(),
          focusX,
          focusY,
        }),
      });
      navigate(ADMIN_HOME_HERO_PHOTOS_PATH);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed — try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <Link className="admin-back-link" to={ADMIN_HOME_HERO_PHOTOS_PATH}>
            ← Home Page Photo Show
          </Link>
          <p className="admin-eyebrow">EDIT METADATA</p>
          <h1 className="admin-title admin-title--compact">Carousel photo</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      {loading ? (
        <section className="admin-card">
          <p className="admin-muted">Loading photo…</p>
        </section>
      ) : null}

      {!loading && error && !photo ? (
        <section className="admin-card">
          <p className="admin-error">{error}</p>
          <Link className="admin-button admin-button--ghost" to={ADMIN_HOME_HERO_PHOTOS_PATH}>
            Back to list
          </Link>
        </section>
      ) : null}

      {!loading && photo ? (
        <section className="admin-card">
          <div className="admin-edit-preview">
            <FocalPointPicker
              src={photoUrl(photo)}
              alt={photo.title || "Carousel photo"}
              focusX={focusX}
              focusY={focusY}
              onChange={({ focusX: nextX, focusY: nextY }) => {
                setFocusX(nextX);
                setFocusY(nextY);
              }}
            />
            <label className="admin-button admin-button--ghost admin-story-cover-upload">
              {replacingImage ? "Uploading…" : "Replace image"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                disabled={replacingImage || saving}
                onChange={(event) => void handleReplaceImage(event)}
              />
            </label>
          </div>
          <p className="admin-hint admin-hint--spaced">
            Click or drag the red dot to set the crop anchor. Replace the image without losing title, description, or keywords.
          </p>

          <form className="admin-form" onSubmit={(event) => void handleSave(event)}>
            <div className="admin-field">
              <label className="admin-label" htmlFor="edit-title">
                Title <span className="admin-label-optional">(optional)</span>
              </label>
              <input
                className="admin-input"
                id="edit-title"
                name="title"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label" htmlFor="edit-description">
                Description <span className="admin-label-optional">(optional)</span>
              </label>
              <textarea
                className="admin-input admin-textarea"
                id="edit-description"
                name="description"
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label" htmlFor="edit-keywords">
                Keywords <span className="admin-label-optional">(optional)</span>
              </label>
              <input
                className="admin-input"
                id="edit-keywords"
                name="keywords"
                type="text"
                value={keywords}
                onChange={(event) => setKeywords(event.target.value)}
              />
              <p className="admin-hint">Separate keywords with commas.</p>
            </div>
            {error ? <p className="admin-error">{error}</p> : null}
            <div className="admin-form-actions">
              <button className="admin-button" type="submit" disabled={saving || replacingImage}>
                {saving ? "Saving…" : "Save changes"}
              </button>
              <Link className="admin-button admin-button--ghost" to={ADMIN_HOME_HERO_PHOTOS_PATH}>
                Cancel
              </Link>
            </div>
          </form>
        </section>
      ) : null}
    </div>
  );
}
